/**
 * Tipos wire da Tela 4 (Coleta Estruturada) — Brief 2B B6 (ADR-144).
 *
 * Hand-written (R15) espelhando o serializer REAL do Engine
 * `apps/engine/src/modules/structured-collections/structured-collections.serialization.ts`
 * (confirmado no arquivo, não via agente — lição da Tela 2). NÃO codegen.
 *
 * Achados da triangulação:
 *   - O serializer bate 1:1 com o controller (sem campo extra escondido —
 *     diferente da Tela 8).
 *   - `fields` é um `Record<string, unknown>` LIVRE (Json plano v1). As chaves
 *     `name`/`cpf` são especiais no dispatcher do finalize (ADR-155 Fase 3 —
 *     `buildPersonWritePlan` em `structured-collections/dispatch-plan.ts`,
 *     sucessor do antigo `mapFieldsToPerson`): name→displayName,
 *     cpf→primaryIdentifier (com survivorship); todo o resto → metadata.collected.
 *   - 422 do finalize/cancel/update usa `{ error, reason }` (NÃO `code`):
 *     `structured_collection_empty` e `structured_collection_not_editable`.
 */

/** Status canônico do rascunho (Prisma `StructuredCollectionStatus`). */
export type ColetaStatus = 'in_progress' | 'completed' | 'cancelled' | 'awaiting_confirmation';

/** Desfecho do fluxo de confirmação com o titular (Item 361 F2). */
export type ConfirmationOutcome = 'confirmed' | 'denied_fraud' | 'denied_data' | 'expired';

/**
 * Item 363 F2 (D4) — de onde veio o pedido em relação ao titular VALIDADO.
 * Orientação do painel (hint junto ao "Confirmar com o titular"); não muda o motor.
 */
export type RequesterAuthorization = 'titular' | 'authorized_contact_cadastral' | 'third_party';

/** Wire shape de `SerializedStructuredCollection`. */
export interface StructuredCollection {
  id: string;
  tenantId: string;
  conversationId: string;
  personId: string | null;
  bondId: string | null;
  createdByUserId: string;
  status: ColetaStatus;
  fields: Record<string, unknown>;
  documentIds: string[];
  generatedPendencyId: string | null;
  // Item 361 F2 — confirmação de alteração cadastral com o titular.
  confirmationStage: 'awaiting_step1' | 'awaiting_step2' | null;
  confirmationOutcome: ConfirmationOutcome | null;
  confirmationSoaId: string | null;
  titularConfirmedAt: string | null;
  // Item 363 F2 — null quando a coleta ainda não tem titular identificado.
  requesterAuthorization: RequesterAuthorization | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

/** Body de POST (create) e PATCH (update). Ambos campos opcionais. */
export interface ColetaWritePayload {
  fields?: Record<string, string>;
  documentIds?: string[];
}

/**
 * PR B (ADR-155 D9) — wire de `GET /v1/structured-collections/:id/schema`
 * (espelha `CollectionSchema` em `structured-collections/field-routing.ts`).
 * `source:'default'` ⇒ nenhum `requiredField` declarado no Playbook — o FE
 * renderiza o form legado FIXO (D8 byte-idêntico); `'playbook'` ⇒ a tela
 * DERIVA dos campos declarados (mesma máquina do dispatcher do finalize).
 */
export interface ColetaSchemaField {
  name: string;
  validate: 'cpf' | 'cnpj' | null;
  target:
    | 'person'
    | 'bond'
    | 'bond-participant'
    | 'authorized-contact'
    | 'episode'
    | 'erp-reconcile';
  durability: 'durable' | 'contract-scope' | 'authorization' | 'episodic' | 'external';
  legalBasis: string | null;
  required: boolean;
  /**
   * PR C (ADR-155 D5) — default herdado do último episódio concluído (mesmo
   * bond+pessoa do episódio). O painel pré-preenche campo VAZIO com marca
   * "da última vez — confirme"; o finalize detecta a confirmação server-side.
   */
  inherited: { value: unknown; sourceAppointmentId: string; collectedAt: string | null } | null;
}

export interface ColetaSchema {
  source: 'playbook' | 'default';
  fields: ColetaSchemaField[];
}

/**
 * Códigos de 422 conhecidos do backend (`{ error, reason }`). `string` para
 * tolerar códigos novos; os dois conhecidos são mapeados em `coletaErrorMessage`.
 */
export type ColetaBlockedCode =
  | 'structured_collection_empty'
  | 'structured_collection_not_editable';

/**
 * PR B — um erro por-campo do dispatcher (ADR-155 Fase 3): o body do 422 do
 * finalize traz `field`/`reason` do primeiro culpado + `allErrors` com todos.
 */
export interface ColetaRouteError {
  field: string;
  error: string;
  reason: string;
}

/** Erro tipado lançado pelo api-client quando finalize/cancel/update devolve 422. */
export class ColetaError extends Error {
  readonly code: string;
  readonly reason: string;
  /** PR B — campo culpado nomeado pelo dispatcher (só nos 422 de roteamento). */
  readonly field: string | null;
  /** PR B — todos os erros por-campo do dispatcher (quando o body os traz). */
  readonly allErrors: ColetaRouteError[] | null;

  constructor(
    code: string,
    reason: string,
    message: string,
    field: string | null = null,
    allErrors: ColetaRouteError[] | null = null,
  ) {
    super(message);
    this.name = 'ColetaError';
    this.code = code;
    this.reason = reason;
    this.field = field;
    this.allErrors = allErrors;
  }
}
